import React from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';

import { IconRegularDelivery, IBasicStyledProps, theme } from '@notino/react-styleguide-v20';

import { useApplicationRuntimeContext } from '@context/ApplicationRuntimeContext';
import { useMediaQuery } from '@hooks';
import { DEFAULT_CART_FONT_SIZE_TEXT_IN_REM } from '@styles/constants';
import { ReactFCV } from '@types';
import { CypressTestNamespace } from '@utils/test';

import { ProgressBar } from '../ProgressBar';

import { StyledFreeDeliveryWrapper, StyledTextRow, StyledText, StyledCheckoutPrice, StyledIconWrapper } from './styled';

export interface IFreeDeliveryProgressBarWrapper extends IBasicStyledProps {
  price: number;
  maxLimit: number;
  messages: {
    achievedFreeShipping: MessageDescriptor;
    amountUntilFreeDelivery: MessageDescriptor;
  };
}

export const FreeDeliveryProgressBarWrapper: ReactFCV<IFreeDeliveryProgressBarWrapper> = React.memo(
  ({ price, maxLimit, messages, ...rest }) => {
    const isCompleted = maxLimit <= price;
    const {
      applicationSettings: { secondaryCurrency },
    } = useApplicationRuntimeContext();

    const isDualPriceLayout = Boolean(secondaryCurrency);
    const mobileView = useMediaQuery(`(max-width: ${theme.breakpoints.sm}rem)`);
    return (
      <StyledFreeDeliveryWrapper data-testid="free-delivery-progress-bar" {...rest}>
        <StyledTextRow>
          <StyledIconWrapper>
            <IconRegularDelivery color={isCompleted ? 'icon.positive' : 'icon.primary'} width="20px" height="20px" />
          </StyledIconWrapper>
          <StyledText isCompleted={isCompleted}>
            {isCompleted ? (
              <FormattedMessage {...messages.achievedFreeShipping} />
            ) : (
              <FormattedMessage
                {...messages.amountUntilFreeDelivery}
                values={{
                  price: (
                    <StyledCheckoutPrice
                      firstCurrency={{
                        priceSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                        currencySize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                        bold: true,
                      }}
                      secondCurrency={{
                        priceSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                        currencySize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                        bold: true,
                      }}
                      separator={{
                        separatorSize: DEFAULT_CART_FONT_SIZE_TEXT_IN_REM,
                        bold: true,
                      }}
                      price={maxLimit - price}
                      isColumnLayout={mobileView && isDualPriceLayout}
                    />
                  ),
                }}
              />
            )}
          </StyledText>
        </StyledTextRow>
        <ProgressBar
          className={CypressTestNamespace.EFreeDeliveryProgressBar.FREE_DELIVERY_PROGRESS_BAR}
          min={0}
          max={maxLimit}
          value={isCompleted ? maxLimit : price}
          undoneColor={'background.tertiary'}
          successColor={'common.green50'}
        />
      </StyledFreeDeliveryWrapper>
    );
  },
);
